/**
 * Data Explorer Module
 * 
 * Advanced data visualization and exploration interface for contract analysis
 * Features multi-dimensional filtering, hierarchical tree navigation, and saved view management
 * 
 * Main Components:
 * 1. DataExploer: Main dashboard with overview and saved filters
 * 2. DataExploerDetailView: Detail view with filtering, column management, and data export
 * 3. TreeView: Hierarchical tree component for navigating dimensional data
 * 4. TreeNode: Individual tree node with expand/collapse and navigation
 * 5. DataTable: Responsive table display with status indicators
 * 6. SummaryStrip: Summary metrics cards (properties, categories, channels, territories)
 * 
 * Features:
 * - Tree-based navigation with search and auto-expand on match
 * - Multi-level hierarchical filtering (L1, L2, L3)
 * - Saved views/filters with persistent storage
 * - Export filtered data to CSV
 * - Column visibility management
 * - Fuzzy tree search with recursive matching
 * - Expandable saved filter details with badge display
 * - Contract table with status modal exceptions
 * 
 * Dependencies:
 * - React Router (useNavigate, useParams, useLocation)
 * - Lucide React icons (ChevronDown, ChevronRight, Plus, Edit3, etc.)
 * - Bootstrap components (cards, buttons, badges, tables)
 * - Custom components: PageLoader, CommonIcon, MultiSelect
 * - Custom services: getDataExploerDahsboard, getDataExplorerDetails
 * - API utilities: apiCall, API_SERVER_URL
 * - Page access control: hasPageAccess, accessDenied
 * 
 * State Architecture:
 * - Main data store: loaded from getDataExploerDahsboard()
 * - Filters: saved views with hierarchical filter structure
 * - UI state: tab selection, panel visibility, accordion expansion
 * - Filter state: licensor, channel, product levels, territory levels, demographics
 * 
 * API Endpoints:
 * - GET /agent/views/: Load saved filter views
 * - DELETE /agent/views/{filterId}/: Delete saved filter
 * - Other: getDataExplorerDetails() for detail view data
 * 
 * @module DataExplorer
 * @requires react
 * @requires react-router-dom
 * @requires lucide-react
 */

import React, { useEffect, useState,useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams, Link } from "react-router-dom";
import { Search, ChevronDown, ChevronRight, Plus, Edit3, Logs, Layers, ListChecks, Tag, SearchCheck, CalendarClock, User, CheckCircle2, ChevronUp, ArrowLeft } from 'lucide-react';
import { API_SUCCESS_RESPONSE_CODE,API_SUCCESS_RESPONSE_CREATED_CODE } from "../../utils/config_mapping";
import PageLoader from '../common/pageloader';
import {getDataExploerDahsboard} from "../../services/admin"
import $ from "jquery";
import { hasPageAccess,accessDenied } from "../../utils/page_access"; // Import
import Multiselect from 'multiselect-react-dropdown';
import { CommonMultiSelect } from '../common/MultiSelect';
import CommonIcon from '../common/customicon';
import { apiCall } from "../../utils/server_utils";
import { API_SERVER_URL } from "../../utils/config_ip_details";
import HeaderLogo  from "../structure/headerlogo";

/* ========== TREE SEARCH UTILITY ========== */
/**
 * Recursively searches through a tree structure and returns matching nodes with their ancestors.
 * Implements depth-first search with branch pruning for unmatched nodes
 * 
 * Algorithm:
 * 1. Filter root nodes and their descendants recursively
 * 2. For each node, check if it matches search term (case-insensitive substring match)
 * 3. Recursively filter children nodes
 * 4. Include node if: (node matches) OR (any descendant matches)
 * 5. Return pruned tree with matching branches intact
 * 
 * Performance: O(n) where n = total nodes in tree
 * Filters out non-matching branches early to reduce result size
 * 
 * @param {Array<Object>} treeData - Array of tree nodes with structure: {id, label, children: Array, count: Number}
 * @param {string} query - Search term to match against node labels (case-insensitive substring)
 * @returns {Array<Object>} Filtered tree with matching nodes and their ancestors preserved
 * 
 * @example
 * // Search for "online" channel in channel tree
 * const results = searchTree(channelTree, "online");
 * // Returns only branch containing "Online" and its children
 * 
 * @example
 * // Empty query returns original tree
 * const results = searchTree(data, "");
 * // Returns all nodes unchanged
 */
const searchTree = (treeData, query) => {
  // Return original tree if no query
  if (!query || query.trim() === '') {
    return treeData;
  }

  const searchTerm = query.toLowerCase().trim();

  /**
   * Recursive helper function that processes each node
   * Filters children and decides whether to include current node
   * 
   * @param {Object} node - Current node being processed
   * @param {string} node.label - Display label for node
   * @param {Array} [node.children] - Child nodes (recursive structure)
   * @returns {Object|null} Node with filtered children, or null if no match found
   */
  const filterNode = (node) => {
    // Check if current node label matches the search term
    const currentNodeMatches = node.label.toLowerCase().includes(searchTerm);

    // If node has children, recursively filter them
    let filteredChildren = null;
    if (node.children && node.children.length > 0) {
      filteredChildren = node.children
        .map(child => filterNode(child))
        .filter(child => child !== null);
    }

    // Include node if:
    // 1. Current node matches search term, OR
    // 2. Any descendant matches (filteredChildren has items with matches)
    if (currentNodeMatches || (filteredChildren && filteredChildren.length > 0)) {
      return {
        ...node,
        children: filteredChildren && filteredChildren.length > 0 ? filteredChildren : node.children
      };
    }

    // No match found in this branch - prune from results
    return null;
  };

  // Apply filter function to all root nodes and remove null results
  return treeData
    .map(node => filterNode(node))
    .filter(node => node !== null);
};

/* ========== MOCK DATA ========== */
/**
 * Mock data structure for development and fallback scenarios
 * Contains product properties, categories, channels, and territories
 * Each category has icon and sample items
 * 
 * @type {Object}
 */
const mockData = {
  properties: {
    title: "Properties",
    icon: "🏠",
    items: [{ name: "Disney" }, { name: "Marvel" }, { name: "Pixar" }],
  },
  categories: {
    title: "Product Categories",
    icon: "🧢",
    items: [{ name: "Apparel" }, { name: "Toys" }, { name: "Stationery" }],
  },
  channels: {
    title: "Channels",
    icon: "🛒",
    items: [{ name: "Walmart" }, { name: "Target" }, { name: "Amazon" }],
  },
  territories: {
    title: "Territories",
    icon: "🌍",
    items: [{ name: "Europe" }, { name: "North America" }, { name: "Asia" }],
  },
};

/* ========== UTILITY: MOCK CONTRACTS ========== */
/**
 * Generate mock contract data for testing and demonstration
 * Creates array of contract objects with realistic licensing data
 * 
 * Contract fields generated:
 * - id: Unique identifier (1 to count)
 * - contractNumber: Format "CN-{1000+index}"
 * - licensor: Rotating from Disney, Marvel, Pixar
 * - property: Same as licensor
 * - productCategory: Rotating from Apparel, Toys, Stationery
 * - channel: Rotating from Walmart, Target, Amazon
 * - territory: Rotating from Europe, North America, Asia
 * - dates: Fixed startDate 2025-01-01, endDate 2026-01-01
 * - royaltyRate: 10% to 20% (5-9% base * 2)
 * - revenue: $100,000 + (index * $1,000)
 * - status: Rotating from Active, Pending, Expired
 * 
 * @param {number} count - Number of contracts to generate
 * @returns {Array<Object>} Array of mock contract objects
 * 
 * @example
 * const contracts = generateMockContracts(50);
 * // Returns 50 contract objects with cycling values
 */
const generateMockContracts = (count) => {
  const statuses = ["Active", "Pending", "Expired"];
  const licensors = ["Disney", "Marvel", "Pixar"];
  const channels = ["Walmart", "Target", "Amazon"];
  const territories = ["Europe", "North America", "Asia"];
  const products = ["Apparel", "Toys", "Stationery"];

  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    contractNumber: `CN-${1000 + i}`,
    licensor: licensors[i % licensors.length],
    property: licensors[i % licensors.length],
    productCategory: products[i % products.length],
    channel: channels[i % channels.length],
    territory: territories[i % territories.length],
    startDate: "2025-01-01",
    endDate: "2026-01-01",
    royaltyRate: (5 + (i % 5)) * 2,
    revenue: 100000 + i * 1000,
    status: statuses[i % statuses.length],
  }));
};

/* ========== DATA TABLE COMPONENT ========== */
/**
 * Responsive data table component for displaying contracts
 * Features: status indicators with modal, hover highlighting, responsive columns
 * 
 * Columns Displayed:
 * 1. Contract # - Contract identifier
 * 2. Licensor - Licensing party name
 * 3. Property - Licensed property/brand
 * 4. Product Category - Type of product (Apparel, Toys, etc.)
 * 5. Channel - Sales channel (Retail, Online)
 * 6. Territory - Geographic region
 * 7. Exception - Status indicator with modal details
 * 
 * Status Badges:
 * - Active (Green): Contract is currently active
 * - Pending (Orange): Awaiting approval
 * - Expired (Red): Contract has ended
 * 
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.data - Contract records to display
 * @returns {JSX.Element} Table with responsive styling and interactive elements
 */
const DataTable = ({ data }) => {
  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount);

  const getStatusClass = (status) => {
    switch (status) {
      case "Active":
        return "badge bg-success";
      case "Pending":
        return "badge bg-warning text-dark";
      case "Expired":
        return "badge bg-danger";
      default:
        return "badge bg-secondary";
    }
  };

  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle">
        {/* Table headers with sticky positioning */}
        <thead className="table-light sticky-top">
          <tr>
            <th>Contract #</th>
            <th>Licensor</th>
            <th>Property</th>
            <th>Product Category</th>
            <th>Channel</th>
            <th>Territory</th>
            {/* <th>Start</th>
            <th>End</th>
            <th className="text-end">Royalty %</th>
            <th className="text-end">Revenue</th> */}
            <th>Exception</th>
          </tr>
        </thead>
        {/* Table body with status cell modal */}
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={11} className="text-center py-4 text-muted">
                No contracts found. Try adjusting your filters.
              </td>
            </tr>
          ) : (
            data.map((c) => (
              <tr key={c.id} style={{ height: "10px", fontSize: "12px", padding: "4px" }}>
                <td>{c.contractNumber}</td>
                <td>{c.licensor}</td>
                <td>{c.property}</td>
                <td>{c.productCategory}</td>
                <td>{c.channel}</td>
                <td>{c.territory}</td>
                {/* <td>{c.startDate}</td>
                <td>{c.endDate}</td>
                <td className="text-end">{c.royaltyRate}%</td>
                <td className="text-end">{formatCurrency(c.revenue)}</td> */}
                {/* <td><span className={getStatusClass(c.status)}>{c.status}</span></td> */}
                {
                  (() => {
                    // Nested component defined per-row to show a Lucide-style warning icon and modal.
                    /**
                     * Status cell with modal dialog
                     * Shows triangle warning icon with modal on click
                     * Color coded by status: Green=Active, Orange=Pending, Red=Expired
                     * 
                     * @component
                     * @param {Object} props
                     * @param {string} props.status - Contract status
                     */
                    function StatusCell({ status }) {
                      const [open, setOpen] = React.useState(false);

                      const color = status === "Active" ? "#10b981" : status === "Pending" ? "#f59e0b" : "#ef4444";
                      const message =
                        status === "Active"
                          ? "This contract is active."
                          : status === "Pending"
                          ? "This contract is pending approval."
                          : "This contract has expired.";

                      return (
                        <>
                          {/* Status icon button */}
                          <button
                            type="button"
                            className="btn btn-sm btn-link p-0"
                            title="View status"
                            onClick={() => setOpen(true)}
                            style={{ lineHeight: 0 }}
                          >
                            {/* Lucide-style AlertTriangle (inline SVG) */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                              <line x1="12" y1="9" x2="12" y2="13"></line>
                              <line x1="12" y1="17" x2="12.01" y2="17"></line>
                            </svg>
                          </button>

                          {/* Modal overlay and card for status details */}
                          {open && (
                            <div
                              role="dialog"
                              aria-modal="true"
                              onClick={() => setOpen(false)}
                              style={{
                                position: "fixed",
                                inset: 0,
                                background: "rgba(0,0,0,0.4)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 2100,
                                padding: 12,
                              }}
                            >
                              <div
                                className="card"
                                onClick={(e) => e.stopPropagation()}
                                style={{ maxWidth: 420, width: "100%" }}
                              >
                                <div className="card-header d-flex justify-content-between align-items-center">
                                  <div className="fw-semibold">Exception</div>
                                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setOpen(false)} />
                                </div>
                                <div className="card-body">
                                  <div className="d-flex align-items-start gap-3">
                                    <div style={{ flex: "0 0 36px", marginTop: 2 }}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                        <line x1="12" y1="9" x2="12" y2="13"></line>
                                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                      </svg>
                                    </div>
                                    <div>
                                      <div className="text-muted small mt-1">{message}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    }

                    return <td><StatusCell status={c.status} /></td>;
                  })()
                }
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};


/* ========== PANELS CONFIGURATION ========== */
/**
 * Panel configuration for data dimensions
 * Each panel represents a data category with icon and label
 * Used for tab navigation and section headers
 * 
 * @type {Array<Object>}
 */
  const Panels = [
    { label: "Licensors & Properties",icon: "🏠" },
    { label: "Categories",icon: "🧢" },
    { label: "Channels",icon: "🛒" },
    { label: "Territories", icon: "🌍" },
  ];


/* ========== SUMMARY METRICS COMPONENT ========== */
/**
 * Summary strip component displaying key metrics
 * Shows 4 metric cards: Properties, Categories, Channels, Territories
 * Each card displays count and icon
 * 
 * Card Layout:
 * - Left (md=3, responsive to full-width on mobile)
 * - Icon on left with image (property) or emoji
 * - Count and label on right
 * - Bootstrap card with shadow and minimal border
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.data - Data object containing count fields
 * @param {number} props.data.properties_count - Total properties
 * @param {number} props.data.products_count - Total categories
 * @param {number} props.data.channels_count - Total channels
 * @param {number} props.data.territories_count - Total territories
 * @returns {JSX.Element} 4-column metric grid
 */
const SummaryStrip = (data) => {


  const totals = {
    properties: mockData.properties.items.length,
    categories: mockData.categories.items.length,
    channels: mockData.channels.items.length,
    territories: mockData.territories.items.length,
  };

  const items = [
    { label: "Properties",icon: "🏠" },
    { label: "Categories",icon: "🧢" },
    { label: "Channels",icon: "🛒" },
    { label: "Territories", icon: "🌍" },
  ];

  return (
    <>
    <div className="row ">
        <div className="col-6 col-md-3 " key={items[0].label}>
  <div className="card border-0 shadow-sm" style={{ minHeight: "80px" }}>
    <div className="card-body d-flex align-items-center justify-content-start py-2 px-3">
      <div className="fs-2 text-primary me-3"><img src="/images/property.png" alt="Logo" width={"50px"} />
</div>
      <div className="text-start">
        <h5 className="card-title mb-0">{data.data.properties_count}</h5>
        <p className="card-text text-muted mb-0 small">{items[0].label}</p>
      </div>
    </div>
  </div>
</div>

     <div className="col-6 col-md-3 " key={items[1].label}>
  <div className="card border-0 shadow-sm" style={{ minHeight: "80px" }}>
    <div className="card-body d-flex align-items-center justify-content-start py-2 px-3">
      <div className="fs-2 text-primary me-3">{items[1].icon}</div>
      <div className="text-start">
        <h5 className="card-title mb-0">{data.data.products_count}</h5>
        <p className="card-text text-muted mb-0 small">{items[1].label}</p>
      </div>
    </div>
  </div>
</div>

<div className="col-6 col-md-3 " key={items[2].label}>
  <div className="card border-0 shadow-sm" style={{ minHeight: "80px" }}>
    <div className="card-body d-flex align-items-center justify-content-start py-2 px-3">
      <div className="fs-2 text-primary me-3">{items[2].icon}</div>
      <div className="text-start">
        <h5 className="card-title mb-0">{data.data.channels_count}</h5>
        <p className="card-text text-muted mb-0 small">{items[2].label}</p>
      </div>
    </div>
  </div>
</div>

<div className="col-6 col-md-3 " key={items[3].label}>
  <div className="card border-0 shadow-sm" style={{ minHeight: "80px" }}>
    <div className="card-body d-flex align-items-center justify-content-start py-2 px-3">
      <div className="fs-2 text-primary me-3">{items[3].icon}</div>
      <div className="text-start">
        <h5 className="card-title mb-0">{data.data.territories_count}</h5>
        <p className="card-text text-muted mb-0 small">{items[3].label}</p>
      </div>
    </div>
  </div>
</div>

    </div>
    
    </>
  );
};

/* ========== MAIN DATA EXPLORER COMPONENT ==========
 * 
 * Dashboard with hierarchical data exploration and saved views
 * 
 * Component Tree:
 * DataExploer (main)
 *  ├── TabPanel 1: Overview (4 TreeView panels)
 *  │    ├── TreeView (Properties)
 *  │    ├── TreeView (Categories)
 *  │    ├── TreeView (Channels)
 *  │    └── TreeView (Territories)
 *  └── TabPanel 2: Saved Views (list of saved filters)
 * 
 * Features:
 * - Two-tab interface: Overview (hierarchical tree) + Saved Views (filter list)
 * - API-driven data loading with error handling
 * - Saved filters with expandable details
 * - Dynamic tree rendering with auto-expand on search
 * - Tab persistence in component state
 * 
 * @component
 * @returns {JSX.Element} Div with two-tab interface and error handling
 */
export const DataExploer = () => {
  // ========== STATE MANAGEMENT ==========
  
  // Core data state
  const [data, setData] = useState([]); // Full dashboard data from API (properties, categories, channels, territories)
  const [filteredData, setFilteredData] = useState([]); // Currently displayed filtered data
  const [loading, setLoading] = useState(true); // Page load indicator
  
  // Tab and filter state
  const [activeTab, setActiveTab] = useState("tab1"); // Active tab: "tab1" (Overview) or "tab2" (Saved Views)
  const [savedFilters, setSavedFilters] = useState([]); // Array of saved filter objects from API
  const [expandedFilters, setExpandedFilters] = useState({}); // Object tracking expanded state of each filter {filterId: boolean}
  const [loadingFilters, setLoadingFilters] = useState(false); // Loading state for saved filters API call
  
  // AI query state
  const [query, setQuery] = useState(""); // User input for AI-powered filtering (e.g., "Show me Disney in Europe")
  
  // Router hooks
  const navigate = useNavigate();

  /* ========== EVENT HANDLERS ========== */
  
  /**
   * Handle user's AI query submission
   * Validates query is not empty, then triggers data fetch with the query
   * 
   * Flow:
   * 1. Check query is not empty
   * 2. Show alert if empty (guide user with example)
   * 3. Call fetchData() with trimmed query string
   * 
   * @function handleAsk
   * @returns {void}
   * 
   * @example
   * // User clicks "Ask AI" button
   * handleAsk(); // Calls fetchData("Show me Disney in Europe")
   */
  const handleAsk = () => {
    if (!query.trim()) {
      // Provide user guidance with example query format
      alert("Please enter a query, e.g. 'Show me Disney in Europe'");
      return;
    }
    else {
      // Trigger data fetch with AI query
      fetchData(query.trim())
    }
  };

  /* ========== API FUNCTIONS ========== */
  
  /**
   * Fetch data explorer dashboard data from server
   * Optionally filters by AI-generated query
   * 
   * API Endpoint: getDataExploerDahsboard(query)
   * Response format: { code, data: { properties_count, products_count, channels_count, territories_count, ... } }
   * 
   * Error Handling:
   * - Success: Updates state with dashboard data
   * - Failure: Logs error to console, displays UI error state
   * 
   * @async
   * @param {string} [askaiquery=""] - Optional AI query to filter data (e.g., "Disney in Europe")
   * @returns {Promise<void>}
   * 
   * @example
   * // Load full dashboard
   * fetchData();
   * 
   * @example
   * // Load filtered by AI query
   * fetchData("Show me active contracts");
   */
   const fetchData = async (askaiquery="") => {
        try {
          // Call API to get dashboard data
          const result = await getDataExploerDahsboard(askaiquery);
          if (result.code === API_SUCCESS_RESPONSE_CODE) {
              setData(result.data);
              setFilteredData(result.data); // Set initial data for filtering
          } 
          else {
            console.warn("Unexpected response status:", result.status);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
        finally {
           setLoading(false); // Stop loading spinner
       }
   };

  /* ========== EFFECT HOOKS ========== */
  
  /**
   * Initialize dashboard on component mount
   * Loads main dashboard data and saved filters in parallel
   * 
   * Lifecycle:
   * 1. Component mounts
   * 2. fetchData() - Load dashboard with metrics
   * 3. loadSavedFilters() - Load saved filter views
   * 4. Loading indicators clear when both complete
   * 
   * Empty dependency array [] means this runs exactly once on mount
   * 
   * @function useEffect
   */
   useEffect(() => {
    // Load main dashboard data
    fetchData();
    // Load saved filters from API
    loadSavedFilters();
   }, []); // Empty dependency array ensures this runs only once on mount

  /**
   * Load saved filter views from API endpoint
   * Retrieves list of previously saved filter configurations
   * 
   * API Endpoint: GET /agent/views/
   * Response format: Array of filter objects with structure:
   *   {
   *     id: number,
   *     name: string,
   *     filters: Array,
   *     columns: Array,
   *     createdAt: string
   *   }
   * 
   * Error Handling:
   * - Success: Populate savedFilters state with API response
   * - Failure: Log error, set empty array as fallback
   * - Finally: Set loading indicator to false
   * 
   * @async
   * @function loadSavedFilters
   * @returns {Promise<void>}
   * 
   * @example
   * // Load on mount or tab change
   * loadSavedFilters();
   */
  const loadSavedFilters = async () => {
    setLoadingFilters(true);
    try {
      // Call API endpoint to get list of saved filter views
      const response = await apiCall("GET", API_SERVER_URL + "/agent/views/");
      
      // Debug: log API response structure
      console.log("Load Saved Filters Response:", response);
      
      if (response.code === API_SUCCESS_RESPONSE_CODE && response.data) {
        // Convert API response to array format
        const filtersArray = Array.isArray(response.data) ? response.data : [];
        setSavedFilters(filtersArray);
      }
    } catch (error) {
      console.error("Error loading saved filters:", error);
      // Fallback to empty array on error
      setSavedFilters([]);
    } finally {
      setLoadingFilters(false);
    }
  };

  /**
   * Delete a saved filter view by ID
   * Prompts user for confirmation before deletion
   * Reloads saved filters list after successful deletion
   * 
   * API Endpoint: DELETE /agent/views/{filterId}/
   * 
   * Flow:
   * 1. Confirm deletion with user (window.confirm)
   * 2. Return early if user cancels
   * 3. Call DELETE API endpoint with filter ID
   * 4. On success: Reload saved filters list
   * 5. On failure: Show error alert with response message
   * 6. On error: Log error and show error dialog
   * 
   * @async
   * @function handleDeleteSavedFilter
   * @param {number|string} filterId - ID of the filter to delete
   * @returns {Promise<void>}
   * 
   * @example
   * // Delete filter with ID 42
   * handleDeleteSavedFilter(42);
   */
  const handleDeleteSavedFilter = async (filterId) => {
    // Confirm deletion with user
    if (!window.confirm('Are you sure you want to delete this saved view?')) {
      return;
    }

    try {
      // Call DELETE API endpoint with filter ID
      const response = await apiCall("DELETE", API_SERVER_URL + `/agent/views/${filterId}/`);
      
      // Debug: log API response
      console.log("Delete Filter Response:", response);
      
      // Reload saved filters on success
      if (response.code === API_SUCCESS_RESPONSE_CODE) {
        loadSavedFilters();
      //  alert('Filter deleted successfully!');
      } else {
        alert('Failed to delete filter. Please try again.');
      }
    } catch (error) {
      console.error("Error deleting filter:", error);
      alert('Error deleting filter: ' + error.message);
    }
  };

  /* ========== RENDER: MAIN JSX ========== */
  
  /**
   * Main JSX render structure
   * 
   * Layout:
   * 1. Page header with title and breadcrumbs
   * 2. Error container (for API errors)
   * 3. "Ask Genie" AI input section (currently hidden with d-none)
   * 4. Tab navigation (Overview | Saved Views)
   * 5. Tab content panels:
   *    - Tab 1 (Overview): 4 TreeView panels for data exploration
   *    - Tab 2 (Saved Views): List of saved filters with expand/collapse
   * 
   * Responsive: Bootstrap grid (container-fluid, row, col-md-*)
   */
  return(
    <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
      <div className="page-content">
        <div className="container-fluid">
          <div id="table_height">
            {/* Page header with title - Currently commented out */}
            {/* <div className="flex-grow-1" id="row_heading">
              <div className="page-title"><Logs className="me-2" size={28} />Data Explorer</div>
              <p className="text-muted my-2 ff-secondary">
                Explore and manage your business data
              </p>
            </div> */}

            {/* Error container - displays API errors if any */}
<div className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <div className="bg-primary text-white rounded p-2 me-3">
                <CommonIcon iconName="Telescope" size={"24px"}  color="#fff" />
              </div>
              <div className="flex-grow-1">
                <h1 className="h3 mb-0 fw-bold">Data Explorer</h1>
                <p className="text-muted mb-0 small">Explore and manage your business data</p>
              </div>
     <div className="ms-auto bg-primary" style={{padding:"1px",border:"1px solid #000",position:"absolute", right:"5px",borderRadius:"2px",top:"15px"}}>
    <img src="../images/predatica_logo.png" alt="Logo" height={"20px"}  /> 
    </div>
            </div>  

          </div>
            <div id="app_err"></div>
          </div>

      <div className="mb-3 p-4 border rounded bg-light d-none">
  <h5 className="mb-3">
    <span role="img" aria-label="ai">💬</span> Ask AI
  </h5>
  <div className="d-flex align-items-center gap-2">
    <input
      type="text"
      className="form-control"
      placeholder="Show me Disney properties sold through Target"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && handleAsk()}
      style={{ flex: 1 }}
    />
    <button
      className="btn btn-primary"
      onClick={handleAsk}
      style={{ whiteSpace: "nowrap", height: "100%" }}
    >
      Ask AI
    </button>
  </div>
</div>

    {loading ? (<PageLoader />) :  <>
    <SummaryStrip data={data} />
    
    {/* Tab Navigation */}
    <div className="d-flex mt-4">
      <div className="card">
        <div className="card-body" style={{padding:"5px"}}>
          <ul className="nav nav-pills animation-nav profile-nav gap-2 gap-lg-3 flex-grow-1" role="tablist">
            <li className="nav-item">
              <a 
                className={`nav-link fs-14 ${activeTab === 'tab1' ? 'active' : ''}`} 
                data-bs-toggle="tab" 
                href="javascript:void(0)" 
                role="tab"
                onClick={() => setActiveTab('tab1')}
              >
                <span className="d-flex align-items-center gap-2">
                  <CommonIcon iconName="FileText" size={"17px"} color={activeTab === 'tab1' ? '#fff' : '#000'} />
                  Overview
                </span>
              </a>
            </li>
            <li className="nav-item">
              <a 
                className={`nav-link fs-14 ${activeTab === 'tab2' ? 'active' : ''}`} 
                data-bs-toggle="tab" 
                href="javascript:void(0)" 
                role="tab"
                onClick={() => setActiveTab('tab2')}
              >
                <span className="d-flex align-items-center gap-2">
                  <CommonIcon iconName="Rows3" size={"17px"} color={activeTab === 'tab2' ? '#fff' : '#000'} />
                  Saved Views
                </span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-body">
            <div className="row">
              <div className="col-md-12">
                <div className="tab-content pt-0 text-muted">
                  {/* ========== TAB 1: OVERVIEW ========== */}
                  {/* Main data exploration interface with 4 hierarchical tree panels */}
                  {/* Layout: 2-column responsive grid with Properties/Categories/Channels/Territories */}
                  <div className={`tab-pane ${activeTab === 'tab1' ? 'active' : ''}`} id="tab1" role="tabpanel">
                    <div className="row">
                      {/* Properties Panel - Licensors & Properties tree */}
                      <div className="col-md-6">
                        <div className="card shadow-sm">
                          <div className="card-header bg-white fw-bold d-flex align-items-center gap-2">
                            <img src="/images/property.png" alt="Logo" width={"30px"} />
                            <span>{Panels[0].label}</span>
                          </div>
                          {/* TreeView component: displays hierarchical property data with search */}
                          <TreeView searchtitle={"Search Properties"} data={data.properties} />
                        </div>
                      </div>

                      {/* Categories Panel - Product categories tree */}
                      <div className="col-md-6">
                        <div className="card shadow-sm">
                          <div className="card-header bg-white fw-bold">
                            {Panels[1].icon} {Panels[1].label}
                          </div>
                          {/* TreeView component: displays hierarchical product categories with search */}
                          <TreeView searchtitle={"Search Categories"} data={data.products} />
                        </div>
                      </div>

                      {/* Channels Panel - Sales channels tree */}
                      <div className="col-md-6">
                        <div className="card shadow-sm">
                          <div className="card-header bg-white fw-bold">
                            {Panels[2].icon} {Panels[2].label}
                          </div>
                          {/* TreeView component: displays hierarchical channels with search */}
                          <TreeView searchtitle={"Search Channels"} data={data.channel} />
                        </div>
                      </div>

                      {/* Territories Panel - Geographic territories tree */}
                      <div className="col-md-6">
                        <div className="card shadow-sm">
                          <div className="card-header bg-white fw-bold">
                            {Panels[3].icon} {Panels[3].label}
                          </div>
                          {/* TreeView component: displays hierarchical territories with search */}
                          <TreeView searchtitle={"Search Territories"} data={data.territories} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ========== TAB 2: SAVED VIEWS ========== */}
                  {/* List of previously saved filter configurations with details and management */}
                  <div className={`tab-pane ${activeTab === 'tab2' ? 'active' : ''}`} id="tab2" role="tabpanel">
                    {/* Loading spinner - shown while fetching saved filters from API */}
                    {loadingFilters ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="mt-3">Loading Filters...</h5>
                        <p className="text-muted">Please wait while we fetch your saved filters.</p>
                      </div>
                    ) : savedFilters.length === 0 ? (
                      /* Empty state - when no saved filters exist */
                      <div className="text-center py-5">
                        <div className="d-flex justify-content-center mb-3">
                          {/* Funnel icon SVG */}
                          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="bi bi-funnel text-muted" viewBox="0 0 16 16">
                            <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2zm1 .5v1.308l4.372 4.858A.5.5 0 0 1 7 8.5v5.306l2-.666V8.5a.5.5 0 0 1 .128-.334L13.5 3.308V2h-11z"/>
                          </svg>
                        </div>
                        <h5>No Saved Filters Yet</h5>
                        <p className="text-muted">Saved filters will appear here once you create them.</p>
                      </div>
                    ) : (
                      /* Saved filters list */
                      <div className="row">
                        {/* Map over savedFilters array and render each filter with expand/collapse */}
                        {savedFilters.map((filter) => {
                          // Track expanded state for this filter
                          const isExpanded = expandedFilters[filter.id];
                          return (
                          <div key={filter.id} className="col-12 mb-1 pb-1 border-bottom">
                          
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <div className="d-flex align-items-center">
                                    {/* Expand/Collapse Button */}
                                    <a
                                      href="javascript:void(0)"
                                      className="me-2"
                                      onClick={() => {
                                        setExpandedFilters(prev => ({
                                          ...prev,
                                          [filter.id]: !prev[filter.id]
                                        }));
                                      }}
                                      style={{ textDecoration: 'none', color: '#000', lineHeight: 0 }}
                                    >
                                      {isExpanded ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <polyline points="6 9 12 15 18 9"/>
                                        </svg>
                                      ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <polyline points="9 18 15 12 9 6"/>
                                        </svg>
                                      )}
                                    </a>
                                    <div>
                                      <span 
                                        className="mb-1 text-muted" 
                                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                        onClick={() => {
                                          navigate(`/data-explorer-details/viewid/${filter.id}/viewname/${filter.name}`);
                                        }}
                                      >
                                        {filter.name}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="d-flex gap-2 flex-shrink-0">
                                    <small className="text-muted d-flex align-items-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                        <line x1="16" y1="2" x2="16" y2="6"/>
                                        <line x1="8" y1="2" x2="8" y2="6"/>
                                        <line x1="3" y1="10" x2="21" y2="10"/>
                                      </svg>
                                      {filter.created_at ? new Date(filter.created_at).toLocaleDateString('en-US', { 
                                        year: 'numeric', 
                                        month: 'short', 
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      }) : 'Date not available'}
                                    </small>

                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDeleteSavedFilter(filter.id)}
                                      title="Delete this filter"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6"/>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                                {/* Show details when expanded */}
                                {isExpanded && (
                                  <>
                                    <div className="border-top pt-2 mt-2">
                                      <small className="text-muted fw-semibold d-block mb-2">Applied Filters:</small>
                                      <div className="d-flex flex-wrap gap-1">
                                        {filter.filters?.licensor?.length > 0 && filter.filters.licensor.map((item, idx) => (
                                          <span key={idx} className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Licensor: {item.label}
                                          </span>
                                        ))}
                                        {filter.filters?.property?.length > 0 && filter.filters.property.map((item, idx) => (
                                          <span key={idx} className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Property: {item.label}
                                          </span>
                                        ))}
                                        {filter.filters?.store && (
                                          <span className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Store: {filter.filters.store.label}
                                          </span>
                                        )}
                                        {filter.filters?.productL1?.length > 0 && filter.filters.productL1.map((item, idx) => (
                                          <span key={idx} className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Product L1: {item.label}
                                          </span>
                                        ))}
                                        {filter.filters?.productL2?.length > 0 && filter.filters.productL2.map((item, idx) => (
                                          <span key={idx} className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Product L2: {item.label}
                                          </span>
                                        ))}
                                        {filter.filters?.productL3?.length > 0 && filter.filters.productL3.map((item, idx) => (
                                          <span key={idx} className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Product L3: {item.label}
                                          </span>
                                        ))}
                                        {filter.filters?.channelL1?.length > 0 && filter.filters.channelL1.map((item, idx) => (
                                          <span key={idx} className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Channel L1: {item.label}
                                          </span>
                                        ))}
                                        {filter.filters?.channelL2?.length > 0 && filter.filters.channelL2.map((item, idx) => (
                                          <span key={idx} className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Channel L2: {item.label}
                                          </span>
                                        ))}
                                        {filter.filters?.channelL3?.length > 0 && filter.filters.channelL3.map((item, idx) => (
                                          <span key={idx} className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Channel L3: {item.label}
                                          </span>
                                        ))}
                                        {filter.filters?.territoryL1?.length > 0 && filter.filters.territoryL1.map((item, idx) => (
                                          <span key={idx} className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Territory L1: {item.label}
                                          </span>
                                        ))}
                                        {filter.filters?.territoryL2?.length > 0 && filter.filters.territoryL2.map((item, idx) => (
                                          <span key={idx} className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Territory L2: {item.label}
                                          </span>
                                        ))}
                                        {filter.filters?.gender?.length > 0 && filter.filters.gender.map((item, idx) => (
                                          <span key={idx} className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Gender: {item.label}
                                          </span>
                                        ))}
                                        {filter.filters?.ageGroup?.length > 0 && filter.filters.ageGroup.map((item, idx) => (
                                          <span key={idx} className="badge rounded-pill" style={{ fontSize: '11px', backgroundColor: '#6366f1', color: 'white' }}>
                                            Age: {item.label}
                                          </span>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Column Visibility Display */}
                                    {filter.showColumns && (
                                      <div className="border-top pt-3 mt-2">
                                        <small className="text-muted fw-semibold d-flex align-items-center mb-2">
                                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                                            <rect x="3" y="3" width="7" height="7"/>
                                            <rect x="14" y="3" width="7" height="7"/>
                                            <rect x="14" y="14" width="7" height="7"/>
                                            <rect x="3" y="14" width="7" height="7"/>
                                          </svg>
                                          Visible Columns ({Object.values(filter.showColumns).filter(v => v).length}/{Object.keys(filter.showColumns).length}):
                                        </small>
                                        <div className="row g-2">
                                          {Object.entries(filter.showColumns).map(([colName, isVisible]) => (
                                            <div key={colName} className="col-6 col-md-4">
                                              <div 
                                                className={`d-flex align-items-center gap-2 p-2 rounded ${isVisible ? 'bg-light bg-opacity-10 border border-light' : 'bg-light border'}`}
                                                style={{ fontSize: '10px' }}
                                              >
                                                {isVisible ? (
                                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#198754" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12"/>
                                                  </svg>
                                                ) : (
                                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                                  </svg>
                                                )}
                                                  <span 
                           style={{ 
                            fontSize: '12px'
                          }}

                          className={`flex-grow-1 ${isVisible ? 'text-muted' : 'text-muted'}`}>
                            {colName}
                          </span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                          
                          </div> 

                        );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>}
  </div></div></div>)
};

/* ========== DETAIL VIEW COMPONENT ==========
 * 
 * Detail view for drilling into specific tree node with filtering and export
 * 
 * Features:
 * - Navigate back to main dashboard
 * - Export filtered contracts to CSV
 * - Slide-out filter panel (Licensor, Channel dropdowns)
 * - Slide-out columns visibility panel
 * - Live table filtering with row count
 * - Contract data table with status indicators
 * - Filter controls component managing state and UI
 * 
 * Data:
 * - Mock contracts data (100 records)
 * - Filtered by property selected in main view
 * - Dynamic filtering by licensor and channel
 * 
 * Layout:
 * - Back button and header
 * - Action buttons (Export, Filter, Columns)
 * - Filter slide panel (right-side drawer)
 * - Columns slide panel (right-side drawer)
 * - Main data table with responsive scrolling
 * - Filter controls and active filter display
 * 
 * @component
 * @returns {JSX.Element} Detail view container with table and panels
 */
export const DataExploerDetailView = () => {
  // ========== STATE & HOOKS ==========
  // React Router hooks
  const location = useLocation();
  const { type,filtervalue } = useParams();
  const { item } = location.state || {};
  const navigate = useNavigate();

  // Show parameter values (for debugging)
  alert(type);
  alert(filtervalue);

  // ========== DATA & FILTERING ==========
  
  /**
   * Generate mock contract records on component mount
   * Creates 100 sample contracts for table display
   */
  const [contracts] = useState(() => generateMockContracts(100));
  
  /**
   * Filter contracts by property name
   * Updates whenever contracts or item changes
   * 
   * Algorithm:
   * - If no item selected: return all contracts
   * - Otherwise: return only contracts matching item.name property
   */
  const filtered = useMemo(() => {
    if (!item) return contracts;
    return contracts.filter((c) => c.property === item.name);
  }, [contracts, item]);

  // ========== SLIDE PANEL STATE ==========
  // Track visibility of filter and columns slide-out panels
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showColumnsPanel, setShowColumnsPanel] = useState(false);

  // ========== API FUNCTIONS ==========
  
  /**
   * Fetch detail view data from API
   * Gets product, channel, territory options and contract data
   * 
   * @async
   * @param {Object} data - Request data
   */
  const getDataExplorerDetail = async (data) => {
                  try {
                    // Call API to get detail view data
                    const result = await getDataExplorerDetails(data);
                    if (result.code === API_SUCCESS_RESPONSE_CODE) {
                      // API response includes data array and dimension options
                      setTableData(result.data || {});
                      setProductOptions(result.data?.product || []);
                      setChannelOptions(result.data?.channel || []);
                      setTerritoryOptions(result.data?.territory || []);
                    } else {
                      console.warn("Unexpected response status:", result.status);
                    }
                  } catch (error) {
                    console.error("Error fetching data:", error);
                  }
                  finally {
                     setLoading(false); // Stop loading spinner
                 }
                };

  // ========== STYLES ==========
  
  /**
   * Generate common style object for slide panels
   * Creates fixed positioning with smooth transition animation
   * 
   * @param {boolean} visible - Whether panel should be visible
   * @returns {Object} CSS style object for slide panel
   */
  const panelCommonStyle = (visible) => ({
    position: "fixed",
    top: "0px",
    right: visible ? 0 : -420,  // Slides in from right when visible
    width: "400px",
    height: "calc(100vh - 80px)",  // Full height except header
    background: "#ffffff",
    boxShadow: "rgba(0, 0, 0, 0.12) 0px 6px 16px",
    transition: "right 220ms ease",  // Smooth slide animation
    zIndex: 1055,
    overflowY: "auto",
    padding: "12px",
    height: "100%"
  });

  /* ========== RENDER: DETAIL VIEW JSX ========== */
  
  return (
    <div className="main-content">
       <HeaderLogo />
      <div className="page-content">
        <div className="container-fluid">
          <div id="table_height">
            {/* Page header */}
            <div className="flex-grow-1" id="row_heading">
              <div className="page-title"><Logs className="me-2" size={28} />Data Explorer</div>
              <p className="text-muted my-2 ff-secondary">
                Explore and manage your business data
              </p>
            </div>
            {/* Error container */}
            <div id="app_err"></div>
          </div>
    <div className="">
      {/* Back button to main dashboard */}
      <button className="btn btn-link mb-3" onClick={() => navigate("/data-explorer")}>
        ← Back to Dashboard
      </button>
      <div className="d-flex align-items-center mb-3">
        <div className="btn-group" role="group" aria-label="actions">
          {/* Export CSV button */}
          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-flex align-items-center"
            onClick={() => {
              // Export current filtered contracts as CSV
              const rows = filtered || [];
              if (!rows.length) {
                alert("No data to export");
                return;
              }
              // Define CSV headers
              const headers = [
                "Contract #",
                "Licensor",
                "Property",
                "Product Category",
                "Channel",
                "Territory",
                "Start",
                "End",
                "Royalty %",
                "Revenue",
                "Status",
              ];
              // Build CSV with proper escaping for quoted fields
              const csv = [
                headers.join(","),
                ...rows.map((r) =>
                  [
                    r.contractNumber,
                    r.licensor,
                    r.property,
                    r.productCategory,
                    r.channel,
                    r.territory,
                    r.startDate,
                    r.endDate,
                    r.royaltyRate,
                    r.revenue,
                    r.status,
                  ]
                    .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
                    .join(",")
                ),
              ].join("\n");
              // Create blob and trigger download
              const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `contracts_export_${new Date().toISOString().slice(0, 10)}.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            }}
            title="Export CSV"
          >
            <ListChecks size={16} className="me-2" /> Export 
          </button>

          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-flex align-items-center"
            onClick={() => {
              setShowFilterPanel(v => !v);
              // ensure only one panel open at a time (optional)
              if (!showFilterPanel) setShowColumnsPanel(false);
            }}
            title="Filter"
          >
            <SearchCheck size={16} className="me-2" /> Filter 
          </button>

          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-flex align-items-center"
            onClick={() => {
              setShowColumnsPanel(v => !v);
              if (!showColumnsPanel) setShowFilterPanel(false);
            }}
            title="Columns"
          >
            <Layers size={16} className="me-2" /> Columns 
          </button>
        </div>
      </div>

      {/* Filter slide panel (right->left, width 200px) */}
      <div style={panelCommonStyle(showFilterPanel)} aria-hidden={!showFilterPanel}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="fw-semibold">Filter</div>
          <button className="btn-close" aria-label="Close" onClick={() => setShowFilterPanel(false)}></button>
        </div>
        <div className="mb-2">
          <label className="form-label small mb-1">Licensor</label>
                        <CommonMultiSelect
                          // options={tab1.licensors || []}
                          // selectedValues={tab1.selectedLicensors || []}
                          // onSelect={(selectedList) => handleSelect("selectedLicensors", selectedList)}
                          // onRemove={(selectedList) => handleSelect("selectedLicensors", selectedList)}
                          displayValue="name"
                          placeholder="Filter Licensors"
                        />        </div>
        <div className="mb-2">
          <label className="form-label small mb-1">Properties</label>
<CommonMultiSelect
                          // options={tab1.licensors || []}
                          // selectedValues={tab1.selectedLicensors || []}
                          // onSelect={(selectedList) => handleSelect("selectedLicensors", selectedList)}
                          // onRemove={(selectedList) => handleSelect("selectedLicensors", selectedList)}
                          displayValue="name"
                          placeholder="Filter Properties"
                        />          </div>

                        <div className="mb-2">
          <label className="form-label small mb-1">Product Categories</label>
<CommonMultiSelect
                          // options={tab1.licensors || []}
                          // selectedValues={tab1.selectedLicensors || []}
                          // onSelect={(selectedList) => handleSelect("selectedLicensors", selectedList)}
                          // onRemove={(selectedList) => handleSelect("selectedLicensors", selectedList)}
                          displayValue="name"
                          placeholder="Filter Product Categories"
                        />          </div>

                        <div className="mb-2">
          <label className="form-label small mb-1">Channels</label>
<CommonMultiSelect
                          // options={tab1.licensors || []}
                          // selectedValues={tab1.selectedLicensors || []}
                          // onSelect={(selectedList) => handleSelect("selectedLicensors", selectedList)}
                          // onRemove={(selectedList) => handleSelect("selectedLicensors", selectedList)}
                          displayValue="name"
                          placeholder="Filter Channels"
                        />          </div>


                        <div className="mb-2">
          <label className="form-label small mb-1">Territories</label>
<CommonMultiSelect
                          // options={tab1.licensors || []}
                          // selectedValues={tab1.selectedLicensors || []}
                          // onSelect={(selectedList) => handleSelect("selectedLicensors", selectedList)}
                          // onRemove={(selectedList) => handleSelect("selectedLicensors", selectedList)}
                          displayValue="name"
                          placeholder="Filter Territories"
                        />          </div>


        <div className="d-flex justify-content-center gap-2 mt-3">
  <button
    className="btn btn-sm btn-primary px-4"
    onClick={() => {
      // apply - implement as needed
      setShowFilterPanel(false);
    }}
  >
    Apply
  </button>
  <button
    className="btn btn-sm btn-outline-primary px-4"
    onClick={() => {
      (document.getElementById("de-filter-licensor") || {}).value = "";
      (document.getElementById("de-filter-channel") || {}).value = "";
    }}
  >
    Clear
  </button>
</div>

      </div>

      {/* Columns slide panel (right->left, width 200px) */}
      <div style={panelCommonStyle(showColumnsPanel)} aria-hidden={!showColumnsPanel}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="fw-semibold">Columns</div>
          <button className="btn-close" aria-label="Close" onClick={() => setShowColumnsPanel(false)}></button>
        </div>
        <div className="small text-muted mb-2">Toggle visible columns</div>
        <div className="d-flex flex-column gap-2">
          {[
            "Contract #",
            "Licensor",
            "Property",
            "Product Category",
            "Channel",
            "Territory",
            "Start",
            "End",
            "Royalty %",
            "Revenue",
            "Status",
          ].map((col, idx) => (
            <div key={col} className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`col-toggle-${idx}`}
                defaultChecked
              />
              <label className="form-check-label small" htmlFor={`col-toggle-${idx}`}>
                {col}
              </label>
            </div>
          ))}
        </div>

        <div className="d-flex justify-content-center gap-2 mt-3">
  <button
    className="btn btn-sm btn-primary px-4"
    onClick={() => {
      // apply - implement as needed
      setShowFilterPanel(false);
    }}
  >
    Apply
  </button>
  <button
    className="btn btn-sm btn-outline-primary px-4"
    onClick={() => {
      (document.getElementById("de-filter-licensor") || {}).value = "";
      (document.getElementById("de-filter-channel") || {}).value = "";
    }}
  >
    Clear
  </button>
</div>



      </div>
          {(() => {
            // Inline component to manage/apply filters and show "clear all"
            const FilterControls = () => {
              const [licensor, setLicensor] = useState("");
              const [channel, setChannel] = useState("");
              const [visibleCount, setVisibleCount] = useState(null);

              const getInputs = () => {
                return {
                  licInput: document.getElementById("de-filter-licensor"),
                  chanInput: document.getElementById("de-filter-channel"),
                };
              };

              const applyFiltersToTable = (lic, chan) => {
                const tbody = document.querySelector(".table-responsive tbody");
                if (!tbody) return;
                let visible = 0;
                tbody.querySelectorAll("tr").forEach((tr) => {
                  const tds = tr.querySelectorAll("td");
                  // Keep the "no data" message row visible if it is the only row
                  if (tds.length === 0) {
                    tr.style.display = "";
                    return;
                  }
                  const licText = (tds[1]?.textContent || "").toLowerCase();
                  const chanText = (tds[4]?.textContent || "").toLowerCase();
                  const matchLic = !lic || lic.trim() === "" || licText.includes(lic.toLowerCase());
                  const matchChan = !chan || chan.trim() === "" || chanText.includes(chan.toLowerCase());
                  const show = matchLic && matchChan;
                  tr.style.display = show ? "" : "none";
                  if (show) visible++;
                });
                setVisibleCount(visible);
              };

              const handleApply = () => {
                const { licInput, chanInput } = getInputs();
                const lic = (licInput && licInput.value) || "";
                const chan = (chanInput && chanInput.value) || "";
                setLicensor(lic);
                setChannel(chan);
                applyFiltersToTable(lic, chan);
              };

              const handleClearAll = () => {
                const { licInput, chanInput } = getInputs();
                if (licInput) licInput.value = "";
                if (chanInput) chanInput.value = "";
                setLicensor("");
                setChannel("");
                applyFiltersToTable("", "");
              };

              // Keep state in sync if user edits the inputs in the slide panel
              useEffect(() => {
                const { licInput, chanInput } = getInputs();
                if (!licInput && !chanInput) return;
                const onLic = () => setLicensor(licInput.value || "");
                const onChan = () => setChannel(chanInput.value || "");
                licInput && licInput.addEventListener("input", onLic);
                chanInput && chanInput.addEventListener("input", onChan);

                // Observe table changes (e.g. when data reloads) and reapply filters
                const tbody = document.querySelector(".table-responsive tbody");
                let observer;
                if (tbody) {
                  observer = new MutationObserver(() => applyFiltersToTable(licensor, channel));
                  observer.observe(tbody, { childList: true, subtree: true });
                }

                // initial apply
                applyFiltersToTable(licensor, channel);

                return () => {
                  licInput && licInput.removeEventListener("input", onLic);
                  chanInput && chanInput.removeEventListener("input", onChan);
                  observer && observer.disconnect();
                };
                // eslint-disable-next-line react-hooks/exhaustive-deps
              }, []); // run once

              return (
                <div className="mb-3 d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2">
                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    {/* Display active filters applied to table */}
                    <div className="small text-muted">Active filters:</div>
                    {licensor ? <span className="badge bg-info text-dark">Licensor: {licensor}</span> : null}
                    {channel ? <span className="badge bg-info text-dark">Channel: {channel}</span> : null}
                    {!licensor && !channel ? <span className="text-muted small">None</span> : null}
                    {/* Show count of visible rows after filtering */}
                    {visibleCount !== null ? (
                      <span className="small text-muted ms-2">Showing: {visibleCount} rows</span>
                    ) : null}
                  </div>

                  <div className="ms-md-auto d-flex gap-2">
                    {/* Button to clear all applied filters */}
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleClearAll}>
                      Clear All
                    </button>
                  </div>
                </div>
              );
            };

            return <FilterControls />;
          })()}
      <DataTable data={filtered} />
    </div></div></div></div>
  );
};

/* ========== RECURSIVE TREE NODE COMPONENT ==========
 * 
 * Individual tree node with hierarchical expand/collapse and navigation
 * 
 * Features:
 * - Recursive rendering of parent and child nodes
 * - Auto-expand on search query match
 * - Count aggregation (direct children + total descendants)
 * - Navigation to detail view on click
 * - Dynamic parameter name mapping based on hierarchy level
 * - Chevron icons for expand/collapse indication
 * 
 * Hierarchy Levels:
 * Properties: Level 1 (Licensor) → Level 2 (Properties)
 * Products: Level 1, 2, 3 (productL1, productL2, productL3)
 * Channels: Level 1, 2, 3 (channelL1, channelL2, channelL3)
 * Territories: Level 1, 2 (territoryL1, territoryL2)
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.node - Tree node object {id, label, children[], count}
 * @param {string} props.title - Category title for parameter name mapping
 * @param {string} props.searchQuery - Active search query for auto-expand logic
 * @param {number} [props.level=1] - Current hierarchy level (1-based)
 * @returns {JSX.Element} Tree node with expand/collapse and navigation
 */
const TreeNode = ({ node, title, searchQuery, level = 1 }) => {
  // ========== STATE ==========
  // Track whether this node is expanded (shows children)
  const [open, setOpen] = useState(false);
  
  // Router hook for navigation
  const navigate = useNavigate();
  
  // Check if node has children for expand/collapse capability
  const hasChildren = (node.children && node.children.length > 0);

  // ========== EFFECTS ==========
  
  /**
   * Auto-expand node when search query matches
   * 
   * Logic:
   * - If search query exists and node has children: expand
   * - If search query cleared: collapse
   * 
   * Dependencies: searchQuery, hasChildren (triggers re-check)
   */
  useEffect(() => {
    if (searchQuery && searchQuery.trim() !== "" && hasChildren) {
      // Search active: auto-expand
      setOpen(true);
    } else if (!searchQuery || searchQuery.trim() === "") {
      // Search cleared: collapse
      setOpen(false);
    }
  }, [searchQuery, hasChildren]);

  // ========== CALCULATIONS ==========
  
  /**
   * Calculate total descendant count recursively
   * 
   * Algorithm:
   * 1. If no children: return node.count
   * 2. Otherwise: sum node.count + sum of all children's getTotalCount()
   * 
   * Used for:
   * - Display total count badge in UI
   * - Hover tooltip showing "Total count: X"
   * 
   * @param {Object} node - Current node in recursion
   * @returns {number} Total count including all descendants
   */
  const getTotalCount = (node) => {
    // Base case: leaf node, return its count
    if (!node.children || node.children.length === 0) {
      return node.count || 0;
    }
    
    // Recursive case: sum children's counts
    const childrenTotal = node.children.reduce((sum, child) => {
      return sum + getTotalCount(child);
    }, 0);
    
    return (node.count || 0) + childrenTotal;
  };

  // Calculate counts for display
  const totalCount = getTotalCount(node);
  const directCount = node.count || 0;
  const childrenCount = node.children ? node.children.length : 0;

  /**
   * Determine API parameter name based on category title and hierarchy level
   * 
   * Mapping Logic:
   * Properties tree (title contains "propert"):
   *   Level 1 → "licensor"
   *   Level 2 → "propertyType"
   * 
   * Product hierarchy (title contains "categor" or "product"):
   *   Level 1 → "productL1"
   *   Level 2 → "productL2"
   *   Level 3 → "productL3"
   * 
   * Channel hierarchy (title contains "channel"):
   *   Level 1 → "channelL1"
   *   Level 2 → "channelL2"
   *   Level 3 → "channelL3"
   * 
   * Territory hierarchy (title contains "territor"):
   *   Level 1 → "territoryL1"
   *   Level 2 → "territoryL2"
   * 
   * Default: Return title if no match
   * 
   * @returns {string} Parameter name for API call
   */
  const getParameterName = () => {
    // Normalize title for comparison
    const titleLower = title.toLowerCase();
    
    if (titleLower.includes('propert')) {
      // Properties/Licensor tree structure
      if (level === 1) return 'licensor';
      if (level === 2) return 'propertyType';
    } else if (titleLower.includes('categor') || titleLower.includes('product')) {
      // Product hierarchy with 3 levels
      if (level === 1) return 'productL1';
      if (level === 2) return 'productL2';
      if (level === 3) return 'productL3';
    } else if (titleLower.includes('channel')) {
      // Channel hierarchy with 3 levels
      if (level === 1) return 'channelL1';
      if (level === 2) return 'channelL2';
      if (level === 3) return 'channelL3';
    } else if (titleLower.includes('territor')) {
      // Territory hierarchy with 2 levels
      if (level === 1) return 'territoryL1';
      if (level === 2) return 'territoryL2';
    }
    
    // Default fallback: use title as parameter name
    return title;
  };

  const paramName = getParameterName();

  return (
    <div className="mb-1">
      <div
        className="d-flex align-items-center justify-content-between small"
        style={{ cursor: hasChildren ? "pointer" : "default", userSelect: "none" }}
      >
        <div className="d-flex align-items-center flex-grow-1">
          <span 
            style={{ width: 18, display: "inline-block" }} 
            className="text-primary" 
            onClick={() => hasChildren && setOpen(v => !v)}
          >
            {hasChildren ? (open ? <ChevronDown size={16} color={"blue"} /> : <ChevronRight size={16} color={"blue"} />) : ""}
          </span>
          <a
            href="#"
            className=""
            style={{ fontSize: "12px", color: "blue", textDecoration: "underline" }}
           onClick={(e) => {
            e.preventDefault();
            const encodedParam = encodeURIComponent(paramName);
            const encodedLabel = encodeURIComponent(node.label);
            navigate(`/data-explorer-details/${encodedParam}/${encodedLabel}`);
            e.stopPropagation();
          }}

          >
            {node.label} <span>{hasChildren && (
            <span 
              className="" 
              style={{ fontSize: "10px", fontWeight: "600" }}
              title={`${childrenCount} ${childrenCount === 1 ? 'child' : 'children'}`}
            >
              ({childrenCount})
            </span>
          )}
          {totalCount > 0 && (
            <span 
              className="" 
              style={{ fontSize: "10px", fontWeight: "600" }}
              title={`Total count: ${totalCount}`}
            >
              {totalCount}
            </span>
          )}</span>
          </a>
        </div>
        
        {/* Count badge */}
        {/* <div className="d-flex align-items-center gap-1 hidden">
          {hasChildren && (
            <span 
              className="badge bg-light text-primary border border-primary" 
              style={{ fontSize: "10px", fontWeight: "600" }}
              title={`${childrenCount} ${childrenCount === 1 ? 'child' : 'children'}`}
            >
              {childrenCount}
            </span>
          )}
          {totalCount > 0 && (
            <span 
              className="badge bg-primary" 
              style={{ fontSize: "10px", fontWeight: "600" }}
              title={`Total count: ${totalCount}`}
            >
              {totalCount}
            </span>
          )}
        </div> */}
      </div>

      {open && (
        <div className="ms-3">
          {node.children && node.children.map(child => (
            <TreeNode 
              key={child.id} 
              node={child} 
              title={title} 
              searchQuery={searchQuery} 
              level={level + 1} 
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ========== TREE VIEW WRAPPER COMPONENT ==========
 * 
 * Top-level tree view component that renders hierarchical data
 * 
 * Features:
 * - Search input with live filtering using searchTree utility
 * - Auto-expands matching nodes on search
 * - Total item count aggregation
 * - Scrollable tree area (280px fixed height)
 * - Empty state handling
 * - Responsive search placeholder
 * 
 * Data Flow:
 * 1. Component receives data array (tree structure)
 * 2. User types in search box → searchQuery state updates
 * 3. filteredData recalculated by searchTree utility
 * 4. TreeNode components re-render with new filteredData
 * 5. TreeNode auto-expands when searchQuery exists
 * 
 * @component
 * @param {Object} props
 * @param {string} props.searchtitle - Placeholder text for search input
 * @param {Array<Object>} props.data - Tree data array with structure {id, label, children[], count}
 * @returns {JSX.Element} Tree view with search and scrollable list
 */
const TreeView = ({ searchtitle, data }) => {
  // ========== STATE ==========
  // Track user's search input
  const [searchQuery, setSearchQuery] = useState("");
  // Track filtered tree data based on search query
  const [filteredData, setFilteredData] = useState(data);

  // ========== EFFECTS ==========
  
  /**
   * Update filtered data when search query or input data changes
   * 
   * Logic:
   * - If no data: set empty array
   * - If no search query: show all data (original tree)
   * - If search query: call searchTree utility to get matching nodes
   * 
   * Dependencies: searchQuery, data (updates when either changes)
   */
  useEffect(() => {
    if (!data) {
      setFilteredData([]);
      return;
    }
    
    if (!searchQuery || searchQuery.trim() === "") {
      // Show original data when search is cleared
      setFilteredData(data);
    } else {
      // Filter tree using searchTree utility function
      const filtered = searchTree(data, searchQuery);
      setFilteredData(filtered);
    }
  }, [searchQuery, data]);

  // Early return if no data provided
  if (!data) return null;

  // ========== EVENT HANDLERS ==========
  
  /**
   * Handle search input change
   * Updates searchQuery state which triggers useEffect to recalculate filteredData
   * 
   * @param {Event} e - Input change event
   */
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // ========== CALCULATIONS ==========
  
  /**
   * Calculate total items count across entire tree (all levels)
   * Sums up all counts and descendant counts recursively
   * 
   * Algorithm:
   * For each root item:
   *   count = item.count + sum of all children's counts recursively
   * Return sum of all root item counts
   * 
   * Used for displaying total badge (e.g., "Total: 45")
   * 
   * @param {Array<Object>} items - Array of tree items
   * @returns {number} Total count of all items including descendants
   */
  const getTotalItems = (items) => {
    if (!items || items.length === 0) return 0;
    
    return items.reduce((total, item) => {
      // Get this item's count
      const itemCount = item.count || 0;
      // Recursively get children's counts
      const childrenCount = item.children ? getTotalItems(item.children) : 0;
      return total + itemCount + childrenCount;
    }, 0);
  };

  // Calculate total items in current filtered data
  const totalItems = getTotalItems(filteredData);

  // ========== RENDER: TREE SECTION ==========
  
  /**
   * Render tree section with search input and scrollable tree area
   * 
   * Layout:
   * 1. Search input with dynamic placeholder
   * 2. Total items badge (right-aligned)
   * 3. Scrollable tree area (fixed height 280px)
   * 4. TreeNode list or empty state message
   * 
   * @param {Array<Object>} items - Tree items to render
   * @param {string} title - Category title for parameter mapping
   * @returns {JSX.Element} Complete tree section with search and list
   */
  const renderSection = (items, title) => (
    <div className="m-3">
      {/* Search input with total items badge */}
      <div className="mb-2 d-flex align-items-center w-100 gap-2">
        <input
          type="search"
          className="form-control form-control-sm flex-grow-1"
          placeholder={searchtitle}
          value={searchQuery}
          onChange={handleSearchChange}
        />
        {totalItems > 0 && (
          <span 
            className="badge bg-primary" 
            style={{ fontSize: "11px", whiteSpace: "nowrap" }}
            title={`Total items: ${totalItems}`}
          >
            Total: {totalItems}
          </span>
        )}
      </div>

      {/* Scrollable tree area with fixed height (280px) */}
      <div style={{ minHeight: 280, maxHeight: 280, overflowY: "auto", paddingRight: 8 }}>
        {items && items.length > 0 ? (
          // Render all root-level TreeNodes
          items.map(item => <TreeNode key={item.id} node={item} title={title} searchQuery={searchQuery} />)
        ) : (
          // Empty state when no items to display
          <div className="text-center text-muted py-4">
            <small>No records found</small>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div className="col-md-12">
        {/* Render search and tree for provided data */}
        {renderSection(filteredData, searchtitle)}
      </div>
    </div>
  );
};
